import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { safeCopy } from "../lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEST_PRESETS = [
    { label: "Homepage", value: "/" },
    { label: "Shop (all products)", value: "/#shop" },
    { label: "Lookbook", value: "/#lookbook" },
    { label: "Drop Countdown", value: "/#countdown" },
    { label: "Manifesto", value: "/#manifesto" },
    { label: "Partners", value: "/#partners" },
    { label: "Ambassador apply", value: "/#ambassador" },
];

const SOURCE_PRESETS = ["ig", "ig_story", "ig_bio", "tiktok", "tiktok_bio", "twitter", "fb", "fb_ads", "email", "youtube", "discord", "podcast"];
const MEDIUM_PRESETS = ["organic", "story", "bio", "ad", "post", "newsletter", "dm", "reel", "video"];

export default function UtmTab({ password }) {
    const [links, setLinks] = useState([]);
    const [stats, setStats] = useState({ rows: [] });
    const [form, setForm] = useState({
        label: "",
        dest_path: "/#shop",
        source: "ig",
        medium: "story",
        campaign: "summer-heat",
        content: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [hours, setHours] = useState(168);

    const load = useCallback(async () => {
        try {
            const [linksRes, statsRes] = await Promise.all([
                axios.get(`${API}/admin/utm-links`, { headers: { "X-Admin-Password": password } }),
                axios.get(`${API}/admin/utm-stats?hours=${hours}`, { headers: { "X-Admin-Password": password } }),
            ]);
            setLinks(linksRes.data || []);
            setStats(statsRes.data || { rows: [] });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load UTM data");
        }
    }, [password, hours]);

    useEffect(() => {
        load();
    }, [load]);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const create = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/admin/utm-links`, form, { headers: { "X-Admin-Password": password } });
            toast.success("UTM link generated");
            setForm((p) => ({ ...p, label: "", content: "" }));
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create link");
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this UTM link?")) return;
        try {
            await axios.delete(`${API}/admin/utm-links/${id}`, { headers: { "X-Admin-Password": password } });
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    const copy = async (url) => {
        const ok = await safeCopy(url);
        if (ok) toast.success("Link copied");
        else toast.error("Copy blocked — please copy manually");
    };

    const statByKey = useMemo(() => {
        const map = new Map();
        for (const r of stats.rows || []) {
            map.set(`${r.source}|${r.medium}|${r.campaign}`, r);
        }
        return map;
    }, [stats]);

    return (
        <div data-testid="utm-tab" className="space-y-6">
            {/* Stats card */}
            <div className="bg-fgm-surface border border-white/10 p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display tracking-[0.3em] text-sm uppercase text-zinc-400">
                        Campaign Performance
                    </h3>
                    <div className="flex items-center gap-2">
                        {[24, 168, 720].map((h) => (
                            <button
                                key={h}
                                data-testid={`utm-range-${h}`}
                                onClick={() => setHours(h)}
                                className={`text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1 border ${
                                    hours === h
                                        ? "bg-[#E63946] border-[#E63946] text-white"
                                        : "border-white/15 text-zinc-400 hover:text-white"
                                }`}
                            >
                                {h === 24 ? "24h" : h === 168 ? "7d" : "30d"}
                            </button>
                        ))}
                    </div>
                </div>
                {(stats.rows || []).length === 0 ? (
                    <p className="text-zinc-500 text-sm">
                        No campaign traffic yet. Generate links below, share them, and stats will populate here as people click.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-white/10">
                                <tr className="text-left">
                                    <th className="py-2 font-display text-[10px] tracking-[0.25em] uppercase text-zinc-500">Source</th>
                                    <th className="py-2 font-display text-[10px] tracking-[0.25em] uppercase text-zinc-500">Medium</th>
                                    <th className="py-2 font-display text-[10px] tracking-[0.25em] uppercase text-zinc-500">Campaign</th>
                                    <th className="py-2 font-display text-[10px] tracking-[0.25em] uppercase text-zinc-500 text-right">Views</th>
                                    <th className="py-2 font-display text-[10px] tracking-[0.25em] uppercase text-zinc-500 text-right">Adds</th>
                                    <th className="py-2 font-display text-[10px] tracking-[0.25em] uppercase text-zinc-500 text-right">Checkouts</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(stats.rows || []).map((r, i) => (
                                    <tr key={i} className="border-b border-white/5">
                                        <td className="py-3 text-white font-mono text-xs">{r.source}</td>
                                        <td className="py-3 text-zinc-400 font-mono text-xs">{r.medium}</td>
                                        <td className="py-3 text-zinc-400 font-mono text-xs">{r.campaign}</td>
                                        <td className="py-3 text-white text-right font-mono">{r.page_view}</td>
                                        <td className="py-3 text-zinc-300 text-right font-mono">{r.add_to_cart}</td>
                                        <td className="py-3 text-[#E63946] text-right font-mono">{r.checkout_clicked}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Generator form */}
            <div className="bg-fgm-surface border border-white/10 p-6">
                <h3 className="font-display tracking-[0.3em] text-sm uppercase text-zinc-400 mb-4">
                    Generate UTM Link
                </h3>
                <form onSubmit={create} className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <label className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 block mb-1">Label</label>
                        <input
                            data-testid="utm-form-label"
                            value={form.label}
                            onChange={(e) => set("label", e.target.value)}
                            placeholder="IG Story Summer Heat"
                            required
                            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 block mb-1">Destination</label>
                        <select
                            data-testid="utm-form-dest"
                            value={form.dest_path}
                            onChange={(e) => set("dest_path", e.target.value)}
                            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white"
                        >
                            {DEST_PRESETS.map((d) => (
                                <option key={d.value} value={d.value}>{d.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 block mb-1">Source</label>
                        <input
                            data-testid="utm-form-source"
                            value={form.source}
                            onChange={(e) => set("source", e.target.value)}
                            list="utm-source-presets"
                            required
                            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white font-mono"
                        />
                        <datalist id="utm-source-presets">
                            {SOURCE_PRESETS.map((s) => <option key={s} value={s} />)}
                        </datalist>
                    </div>
                    <div>
                        <label className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 block mb-1">Medium</label>
                        <input
                            data-testid="utm-form-medium"
                            value={form.medium}
                            onChange={(e) => set("medium", e.target.value)}
                            list="utm-medium-presets"
                            required
                            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white font-mono"
                        />
                        <datalist id="utm-medium-presets">
                            {MEDIUM_PRESETS.map((s) => <option key={s} value={s} />)}
                        </datalist>
                    </div>
                    <div>
                        <label className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 block mb-1">Campaign</label>
                        <input
                            data-testid="utm-form-campaign"
                            value={form.campaign}
                            onChange={(e) => set("campaign", e.target.value)}
                            placeholder="summer-heat"
                            required
                            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white font-mono"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500 block mb-1">Content (optional)</label>
                        <input
                            data-testid="utm-form-content"
                            value={form.content}
                            onChange={(e) => set("content", e.target.value)}
                            placeholder="hero_video"
                            className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white font-mono"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <button
                            data-testid="utm-form-submit"
                            type="submit"
                            disabled={submitting}
                            className="bg-[#E63946] text-white font-display tracking-[0.25em] uppercase text-[10px] px-5 py-2 hover:bg-white hover:text-[#E63946] disabled:opacity-50"
                        >
                            {submitting ? "Generating…" : "+ Generate Link"}
                        </button>
                    </div>
                </form>
            </div>

            {/* Saved links list */}
            <div className="bg-fgm-surface border border-white/10 p-6">
                <h3 className="font-display tracking-[0.3em] text-sm uppercase text-zinc-400 mb-4">
                    Saved Links ({links.length})
                </h3>
                {links.length === 0 ? (
                    <p className="text-zinc-500 text-sm">No links yet — generate one above to start tracking.</p>
                ) : (
                    <div className="space-y-2">
                        {links.map((l) => {
                            const s = statByKey.get(`${l.source}|${l.medium}|${l.campaign}`) || {};
                            return (
                                <div key={l.id} data-testid={`utm-link-${l.id}`} className="border border-white/10 p-3 flex flex-wrap items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display tracking-wider text-sm text-white">{l.label}</div>
                                        <div className="text-[10px] font-display tracking-[0.2em] uppercase text-zinc-500 mt-0.5">
                                            {l.source} · {l.medium} · {l.campaign}
                                            {l.content && ` · ${l.content}`}
                                        </div>
                                        <a
                                            href={l.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs font-mono text-zinc-400 hover:text-[#E63946] break-all block mt-1"
                                        >
                                            {l.url}
                                        </a>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-[10px] font-display tracking-[0.2em] uppercase text-zinc-500">
                                            <span className="text-white font-mono text-sm">{s.page_view || 0}</span> views
                                            {" · "}
                                            <span className="text-[#E63946] font-mono text-sm">{s.checkout_clicked || 0}</span> chk
                                        </div>
                                        <button
                                            data-testid={`utm-link-copy-${l.id}`}
                                            onClick={() => copy(l.url)}
                                            className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1 border border-white/15 text-zinc-300 hover:text-white"
                                        >
                                            Copy
                                        </button>
                                        <button
                                            data-testid={`utm-link-delete-${l.id}`}
                                            onClick={() => remove(l.id)}
                                            className="text-zinc-500 hover:text-[#E63946] text-lg leading-none px-1"
                                            aria-label="Delete"
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
